/**
 * Analytics Tracker for Algolia Insights Events
 * Routes all events through window.Shopify.analytics.publish to be handled by custom pixel
 * checkout_completed event (see algolia-search-insights-pixel.js).
 */

(function () {
  'use strict';

  // Prevent duplicate initialization
  if (window.MagnoliaAnalyticsTracker) {
    return;
  }

  // Constants
  const SESSION_STORAGE_QUERY_ID_KEY = '_mgn_algolia_query_id';
  const SESSION_STORAGE_QUERY_CONTEXT_KEY = '_mgn_algolia_query_context';

  /**
   * Main Analytics Tracker Class
   */
  class AnalyticsTracker {
    constructor() {
      this.userToken = null;
      this.consentGranted = false;
      this.currentQueryId = null;
      this.currentResultContext = null;
      
      this.init();
    }

    /**
     * Initialize tracker
     */
    init() {
      this.checkConsent();
      this.loadStoredQueryId();
      this.setupEventListeners();
    }

    /**
     * Check user consent for tracking
     */
    checkConsent() {
      // Check for common consent management patterns
      
      // Example: OneTrust
      if (window.OnetrustActiveGroups) {
        this.consentGranted = window.OnetrustActiveGroups.includes('C0002'); // Performance cookies
      }
      // Example: Custom consent
      else if (window.userConsent) {
        this.consentGranted = window.userConsent.analytics === true;
      }
      // Default: check for opt-out cookie
      else {
        this.consentGranted = !this.getCookie('analytics_opt_out');
      }
    }

    /**
     * Get or generate user token for Algolia
     */
    getUserToken() {
      // Use customer ID if logged in
      if (window.Shopify?.customerPrivacy?.getShopPrefs?.()?.customer_id) {
        return `customer_${window.Shopify.customerPrivacy.getShopPrefs().customer_id}`;
      }

      // Otherwise generate/retrieve anonymous token
      let token = localStorage.getItem('_mgn_user_token');
      if (!token) {
        token = `anonymous_${this.generateUUID()}`;
        localStorage.setItem('_mgn_user_token', token);
      }
      return token;
    }

    /**
     * Generate UUID for anonymous users
     */
    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    /**
     * Load stored query ID from session storage
     */
    loadStoredQueryId() {
      try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_QUERY_ID_KEY);
        if (stored) {
          this.currentQueryId = stored;
        }

        const storedContext = sessionStorage.getItem(SESSION_STORAGE_QUERY_CONTEXT_KEY);
        if (storedContext) {
          this.currentResultContext = JSON.parse(storedContext);
        }
      } catch (error) {
        console.warn('[Analytics Tracker] Failed to load stored query ID:', error);
      }
    }

    /**
     * Store query ID in session storage
     */
    storeQueryId(queryId, context = null) {
      if (!queryId) return;
      try {
        this.currentQueryId = queryId;
        sessionStorage.setItem(SESSION_STORAGE_QUERY_ID_KEY, queryId);

        if (context) {
          this.currentResultContext = context;
          sessionStorage.setItem(SESSION_STORAGE_QUERY_CONTEXT_KEY, JSON.stringify(context));
        }

      } catch (error) {
        console.warn('[Analytics Tracker] Failed to store query ID:', error);
      }
    }

    /**
     * Get cookie value by name
     */
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    }

    /**
     * Setup global event listeners
     */
    setupEventListeners() {
      // Listen for custom product click events
      document.addEventListener('analytics:product-click', (event) => {
        this.handleProductClick(event.detail);
      });

    }

    /**
     * Track product click
     * Routes through Shopify.analytics.publish to be handled by custom pixel
     */
    handleProductClick(params) {
      // Re-evaluate consent at click time
      this.checkConsent();

      if (!this.consentGranted) {
        return;
      }

      const {
        productId,
        clickPosition,
        queryId,
        resultType,
        sort,
        isPersonalized,
        experienceVariant,
        filterContext = null,
      } = params;

      // Validate required params
      if (!productId || !clickPosition) {
        console.error('[Analytics Tracker] Missing required parameters for product click:', params);
        return;
      }

      if (queryId) {
        // Store query ID for conversion attribution
        this.storeQueryId(queryId, {
          resultType,
          sort,
          isPersonalized,
          experienceVariant,
          filterContext,
          timestamp: Date.now(),
        });
      }

      // Algolia Insights click tracking is handled by collection-grid-analytics.js
      // and algolia-collection.js via AlgoliaInsights.publishClick()
      // This tracker only stores query context for attribution
    }

    /**
     * Clear stored query ID
     */
    clearQueryId() {
      try {
        sessionStorage.removeItem(SESSION_STORAGE_QUERY_ID_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_QUERY_CONTEXT_KEY);
        this.currentQueryId = null;
        this.currentResultContext = null;
      } catch (error) {
        console.warn('[Analytics Tracker] Failed to clear query ID:', error);
      }
    }

    /**
     * Public API: Manually track product click
     */
    trackProductClick(params) {
      this.handleProductClick(params);
    }

  }

  // Initialize global tracker instance
  window.MagnoliaAnalyticsTracker = new AnalyticsTracker();

})();
